import { Injectable } from '@angular/core';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuSearchService as CoreKlevuSearchService } from '@df/module-klevu-search/klevu-search.service';

export interface IKlevuSearchServiceLocalMeta {
    excludeIds: { key: 'id' | 'itemGroupId'; value: string }[];
}
@Injectable({
    providedIn: 'root'
})
export class KlevuSearchService extends CoreKlevuSearchService {
    excludedItemsCache: {
        [categoryId: number]: {
            excludedItems: (string | number)[];
            excludedParents: (string | number)[];
        };
    } = {};

    /**
     * Retrieves method to filter out products from category based on klevu result
     */
    sendCategoryExclusions(category: Category<ICategoryDataV3>): Promise<(products: Product[]) => Product[]> {
        const categoryId = category.id;

        if (this.excludedItemsCache[categoryId]) {
            return Promise.resolve(this.filterProducts(this.excludedItemsCache[categoryId]));
        }

        const request: KlevuSearch.ISortRequest = {
            context: this.getRequestContextPart(),
            recordQueries: [
                {
                    id: 'categoryProducts',
                    typeOfRequest: 'CATNAV',
                    settings: {
                        limit: 1000,
                        fields: ['id'],
                        query: {
                            categoryPath: this.getCategoryPath(category)
                        }
                    }
                }
            ]
        };

        return new Promise((resolve, reject) => {
            this.sendSearchRequest(request)
                .then(response => {
                    const excluded = (response?.queryResults?.[0]?.meta as any as IKlevuSearchServiceLocalMeta)?.excludeIds || [];
                    const excludedItems = excluded.filter((item: any) => item.key === 'id').map(item => parseInt(item.value, 10)) || [];
                    const excludedParents = excluded.filter(item => item.key === 'itemGroupId').map(item => parseInt(item.value, 10)) || [];

                    this.excludedItemsCache[categoryId] = { excludedItems, excludedParents };
                    resolve(this.filterProducts(this.excludedItemsCache[categoryId]));
                })
                .catch(reject);
        });
    }

    private filterProducts({
        excludedItems,
        excludedParents
    }: {
        excludedItems: (string | number)[];
        excludedParents: (string | number)[];
    }): (products: Product<IProductDataV2>[]) => Product[] {
        return (products: Product<IProductDataV2>[]) => {
            return products.filter(product => {
                return !excludedItems.includes(product.id) && !excludedParents.includes(product?.data?.parent || 0);
            });
        };
    }
}
